import { View, StyleSheet, ScrollView } from 'react-native';
import { Button, Checkbox, RadioButtonGroup, Text, theme } from '@socrait/react';

import { Select } from '~/components/Select';
import { type IncludeKey, type MessageFormat, type Timeframe } from '~/services/studentData';

import { IncludeOptions } from './StudentDataScreen';

const FORMAT_OPTIONS = [
  { label: 'Email message', value: 'email' as MessageFormat },
  { label: 'Phone script', value: 'phone' as MessageFormat },
  { label: 'Summary', value: 'summary' as MessageFormat },
];

const TIMEFRAME_OPTIONS: { label: string; value: Timeframe }[] = [
  { label: 'Today', value: 'today' },
  { label: 'This week', value: 'week' },
  { label: 'This month', value: 'month' },
  { label: 'Past 3 months', value: '3months' },
  { label: 'All time', value: 'all' },
];

type GenerateMessagePanelProps = {
  includeOptions: IncludeOptions;
  timeframe: Timeframe;
  format: MessageFormat;
  isDirty: boolean;
  hasAnyInclude: boolean;
  onIncludeChange: (key: IncludeKey, checked: boolean) => void;
  onTimeframeChange: (t: Timeframe) => void;
  onFormatChange: (f: MessageFormat) => void;
  onGenerate: () => void;
};

export function GenerateMessagePanel({
  includeOptions,
  timeframe,
  format,
  isDirty,
  hasAnyInclude,
  onIncludeChange,
  onTimeframeChange,
  onFormatChange,
  onGenerate,
}: GenerateMessagePanelProps) {
  const buttonName = isDirty ? 'Go!' : 'Make a different selection';
  const buttonVariant = isDirty && hasAnyInclude ? 'primary' : undefined;
  const buttonDisabled = !isDirty || !hasAnyInclude;

  const formatDefaultIndex = FORMAT_OPTIONS.findIndex((o) => o.value === format);

  return (
    <ScrollView style={styles.scrollView}>
      <View style={styles.container}>
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>Generate a message about:</Text>
          <View style={[styles.checkboxGroup, styles.sectionContent]}>
            <Checkbox
              label="Praises"
              isChecked={includeOptions.praises}
              onToggle={(checked) => onIncludeChange('praises', checked)}
              labelStyle={styles.optionLabel}
            />
            <Checkbox
              label="Warnings"
              isChecked={includeOptions.warnings}
              onToggle={(checked) => onIncludeChange('warnings', checked)}
              labelStyle={styles.optionLabel}
            />
            <Checkbox
              label="Absences"
              isChecked={includeOptions.attendance}
              onToggle={(checked) => onIncludeChange('attendance', checked)}
              labelStyle={styles.optionLabel}
            />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionLabel}>Generate a message about:</Text>
          <View style={styles.sectionContent}>
            <Select
              value={timeframe}
              onChange={(value) => onTimeframeChange(value as Timeframe)}
              options={TIMEFRAME_OPTIONS}
            />
          </View>
        </View>
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>Message format:</Text>
          <RadioButtonGroup
            key={formatDefaultIndex}
            options={FORMAT_OPTIONS}
            defaultIndex={formatDefaultIndex}
            onSelectOption={(value) => onFormatChange(value as MessageFormat)}
            containerStyle={styles.sectionContent}
            labelStyle={styles.optionLabel}
          />
        </View>
        <Button
          name={buttonName}
          variant={buttonVariant}
          disabled={buttonDisabled}
          onPress={onGenerate}
          containerStyle={styles.generateButton}
          size="large"
        />
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flexGrow: 0,
    width: 250,
  },
  container: {
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing.xlarge,
    flexShrink: 0,
  },
  optionLabel: {
    fontSize: theme.fontSizes.small,
    lineHeight: theme.lineHeights.regular,
  },
  sectionLabel: {
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.bold,
    height: 40, // to match the button height of the message panel
    display: 'flex',
    alignItems: 'center',
  },
  section: {
    flexDirection: 'column',
    gap: theme.spacing.xlarge,
  },
  sectionContent: {
    paddingHorizontal: theme.spacing.xlarge,
  },
  checkboxGroup: {
    flexDirection: 'column',
    gap: theme.spacing.large,
  },
  generateButton: {
    marginTop: theme.spacing.xlarge,
    width: '100%',
  },
});
